import React, { useEffect, useState } from 'react'
import { TextStyle, View, ViewStyle } from 'react-native'
import { useStores, setPageTitle, BLOCKED_FILE_EXTENSIONS } from '@trivie/core'
import { Button, color, Header, TabController } from '@trivie/ui-mobile'
import { observer } from 'mobx-react-lite'
import { Text } from '@trivie/ui-mobile/components/text'
import { AchievementsScreen } from './tabs/achievements-screen'
import { HistoryScreen } from './tabs/history-screen'
import { StatsScreen } from './tabs/stats-screen'
import { BadgesScreen } from './tabs/badges-screen'
import { Controller, useForm } from 'react-hook-form'
import { Avatar } from '@trivie/ui-web/components/avatar'
import { AlertDialog } from '@trivie/ui-web/components/alert-dialog'
import { useHistory } from 'react-router-dom'
import { useMediaQuery } from '@mui/material'

import { SettingsRounded } from '@mui/icons-material'
import { SubscriptionLockedScreen } from '@trivie/ui-mobile/components/subscription-locked-screen'

interface ProfileScreenProps {
  dashboard?: boolean
}
export const ProfileScreen = observer((props: ProfileScreenProps) => {
  const { dashboard } = props
  const { subscriptionStore, userStore, uiStore } = useStores()
  const { enterpriseTier, checkNotifications, getUser, user, uploadProfilePhoto } = userStore

  const [image, setImage] = useState<File | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [activeTab, setActiveTab] = useState(0)

  const { control } = useForm()

  useEffect(() => {
    if (user) {
      getUser(user.id)
    }
    setPageTitle(`Profile - ${process.env.REACT_APP_SITE_TITLE}`)
  }, [])

  const tabs = [
    {
      id: 0,
      name: 'stats',
      label: 'Profile.stats',
      component: <StatsScreen />,
      onPress: () => setActiveTab(0),
      customStyle: { marginLeft: 0 },
      enable: true,
    },
    {
      id: 1,
      name: 'achievements',
      label: 'Profile.achievements',
      component: <AchievementsScreen />,
      onPress: () => setActiveTab(1),
      enable: enterpriseTier,
    },
    {
      id: 2,
      name: 'activity',
      label: 'Profile.activity',
      component: <HistoryScreen />,
      onPress: () => setActiveTab(2),
      enable: enterpriseTier,
    },
    {
      id: 3,
      name: 'badges',
      label: 'Profile.badges',
      component: <BadgesScreen />,
      onPress: () => setActiveTab(3),
      enable: !enterpriseTier,
    },
  ]

  const history = useHistory()
  const isMobile = !useMediaQuery('(min-width:800px)')

  const isSubscriptionWorkspace = userStore.isSubscriptionWorkspace
  const hasActive = subscriptionStore.hasActiveSubscriptions

  return (
    <div style={ROOT}>
      {dashboard && (
        <div style={HEADER_STICKY_CONTAINER}>
          <div style={HEADER_FLEX_CONTAINER}>
            <div style={HEADER_ITEM1}>
              <Header headerTx="Dashboard.header" />
            </div>
          </div>
        </div>
      )}
      <AlertDialog
        open={!!errorMessage}
        title="Upload Failed"
        message={errorMessage || ''}
        buttonText="Close"
        onClose={() => {
          setErrorMessage('')
        }}
      />
      <View style={CONTAINER}>
        <div style={CONTENT}>
          {!dashboard && (
            <View style={PROFILE_CONTAINER}>
              <View style={{ flex: 1 }} />
              <View style={USER_CONTAINER}>
                <Controller
                  control={control}
                  render={({ value }) => {
                    return (
                      <Avatar
                        fontStyle={{ fontFamily: uiStore.branding.font_family, fontSize: 32 }}
                        size={90}
                        name={`${user?.first_name} ${user?.last_name}`}
                        url={image ? URL.createObjectURL(image) : user?.photo_url}
                        onChange={
                          isSubscriptionWorkspace && !hasActive
                            ? undefined
                            : (file: File) => {
                                if (file) {
                                  if (BLOCKED_FILE_EXTENSIONS.includes(file.type)) {
                                    let targetStr = file.type
                                    if (targetStr.includes('/')) {
                                      targetStr = targetStr.split('/')[1]
                                      setErrorMessage(
                                        `Sorry, but we don't support ${targetStr} images`,
                                      )
                                    } else {
                                      setErrorMessage(
                                        `Sorry, but we don't support ${file.type} files`,
                                      )
                                    }
                                  } else {
                                    const FILE_MAX_SIZE = 10 * 1024 * 1024
                                    if (file.size > FILE_MAX_SIZE) {
                                      setErrorMessage(
                                        `File size limit exceeded. Please select a file up to 10 MB.`,
                                      )
                                      return
                                    }
                                    const formData = new FormData()
                                    formData.append('photo', file)
                                    setImage(file)
                                    uploadProfilePhoto(formData, user!.id).then(() => {
                                      checkNotifications(user!.id)
                                    })
                                  }
                                }
                              }
                        }
                      />
                    )
                  }}
                  name="photo"
                  defaultValue=""
                />
                <Text
                  style={PROFILE_TEXT}
                  variant="headline"
                  disableLink
                  text={
                    user?.first_name && user?.last_name
                      ? `${user?.first_name} ${user?.last_name}`
                      : user?.email || ''
                  }
                />
              </View>
              {isMobile && (
                <View
                  style={{
                    flex: 1,
                    alignSelf: 'flex-start',
                    position: 'absolute',
                    top: 12,
                    right: 0,
                  }}
                >
                  <Button
                    variant="text"
                    onPress={() => history.push('/settings')}
                    style={{ alignSelf: 'flex-end', marginRight: 12 }}
                  >
                    <SettingsRounded />
                  </Button>
                </View>
              )}
            </View>
          )}

          {isSubscriptionWorkspace && !hasActive ? (
            <View testID="talk-screen" style={{ flex: 1, height: '100%', maxWidth: 800 }}>
              <div style={{ height: '60%', justifyContent: 'center', display: 'flex' }}>
                <SubscriptionLockedScreen
                  emoji="📚"
                  title="No active subscriptions"
                  helperText="Purchase or renew a subscription to start learning"
                  buttonText="Browse Products"
                  onButtonPress={
                    userStore.externalStoreUrl
                      ? () => {
                          window.open(
                            // @ts-ignore
                            userStore.externalStoreUrl,
                            '_blank',
                            'noopener',
                          )
                        }
                      : undefined
                  }
                />
              </div>
            </View>
          ) : (
            <TabController
              containerStyle={{ paddingLeft: 0, marginBottom: 20, justifyContent: 'flex-start' }}
              setActiveTab={setActiveTab}
              activeTab={activeTab}
              tabs={tabs}
              matchID
            />
          )}
        </div>
      </View>
    </div>
  )
})

/**
 * Styling
 */
const ROOT: React.CSSProperties = {
  height: '100%',
}
const CONTAINER: ViewStyle = {
  backgroundColor: color.white,
  height: '100%',
  display: 'flex',
  // justifyContent: 'center',
  alignItems: 'center',
}
const PROFILE_CONTAINER: ViewStyle = {
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 20,
}
const PROFILE_TEXT: TextStyle = {
  marginTop: 20,
  alignSelf: 'center',
  textAlign: 'center',
}
const USER_CONTAINER: ViewStyle = {
  alignItems: 'center',
  flexDirection: 'column',
  alignSelf: 'center',
  justifyContent: 'center',
  flex: 2,
  paddingTop: 8,
}
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}
const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  paddingTop: 5,
  paddingBottom: 10,
  paddingRight: 15,
  justifyContent: 'space-between',
}
const HEADER_ITEM1: React.CSSProperties = {}
const CONTENT: React.CSSProperties = { width: '100%', height: '100%' }
